import i18n from "i18next";
import "../i18n";
import { account } from "../constants/actionTypes";

// One switch of a Notifications v2 row, written as 0 or 1 whatever the caller passed.
const setPreference = (rows, notification, channel, value) =>
  rows.map(row => (row.notification === notification ? { ...row, [channel]: value ? 1 : 0 } : row));

// A copy of the map without one key.
const withoutKey = (map, key) => Object.fromEntries(Object.entries(map).filter(([existing]) => existing !== key));

const defaultState = {
  error: {
    password: null,
    notifications: null,
    changeEmail: {}
  },
  verificationError: {
    changeEmail: false,
    checkEmailCode: false,
    checkPhoneCode: false,
    changePhone: false
  },
  notificationSettings: { notifications: [], newsletters: [] },
  // Notifications v2 (GROWTH-7692). rowState is keyed `${notification}:${channel}` -> "saving" | "saved" | "error".
  notificationsV2: {
    rows: [],
    channels: { email: "", text: null },
    smsAvailable: false,
    following: null,
    accountLoading: true,
    accountError: null,
    rowState: {},
    rowErrors: {},
    listings: { items: [], total: 0, loading: true, error: null }
  },
  profile: {
    login: "",
    new_messages: "",
    unread_messages: "",
    unread_notifications: "",
    listings_count: "0",
    referral_requests_count: "0",
    referral_requests_pending_import: "0",
    notifications: []
  },
  preloaders: {
    profile: false,
    contacts: false,
    password: false,
    notificationSettings: false,
    notifications: false,
    newsletters: false,
    verifications: false,
    removeNotificationEmail: false,
    removeNotificationPhone: false
  },
  success: {
    contacts: null,
    password: null,
    notifications: null,
    newsletters: null
  },
  modals: {
    changeEmail: false,
    changeEmailVerification: false,
    changeEmailSuccess: false,
    verifyEmail: false,
    verifyEmailSuccess: false,
    addPhone: false,
    addPhoneSuccess: false,
    changePhone: false,
    changePhoneSuccess: false,
    removePhone: false,
    removePhoneSuccess: false,
    changeEmailNew: false,
    changePhoneNew: false,
    changePasswordSuccess: false,
    deleteEmail: false,
    deletePhone: false,
    deletePhoneSuccess: false,
    deleteEmailSuccess: false,
    questionForm: false,
    questionFormSuccess: false
  },
  newLoginData: {
    email: "",
    password: ""
  },
  changeType: "",
  emailVerificationToken: "",
  phoneVerificationToken: ""
};

export default function(state = defaultState, action) {
  const { payload, type } = action;

  switch (type) {
    case account.SET_LOADING:
      return {
        ...state,
        preloaders: { ...state.preloaders, [payload.section]: payload.isSectionLoading },
        verificationError: {
          ...defaultState.verificationError
        }
      };

    case account.GET_PROFILE:
      return {
        ...state,
        profile: payload,
        preloaders: {
          ...state.preloaders,
          profile: false
        }
      };

    // The sidebar Inbox badge and the banner logic read these two; nothing else in the profile is touched (GROWTH-7192).
    case account.SET_INBOX_COUNTS:
      return {
        ...state,
        profile: {
          ...state.profile,
          new_messages: payload.new_messages,
          unread_messages: payload.unread_messages
        }
      };

    // The My Feed "Needs your attention" card re-reads the score when it opens, because the profile is loaded once per
    // session and an edit made since would otherwise leave it stale (GROWTH-7133). Nothing else in the profile is touched.
    case account.SET_PROFILE_COMPLETION:
      return {
        ...state,
        profile: {
          ...state.profile,
          profile_completion: payload
        }
      };

    case account.GET_PROFILE_FAIL:
      return {
        ...state,
        profileFail: payload,
        preloaders: {
          ...state.preloaders,
          profile: false
        }
      };

    case account.UPDATE_CONTACTS:
      return {
        ...state,
        profile: { ...state.profile, ...payload }
      };

    case account.UPDATE_CONTACTS_SUCCESS:
      return {
        ...state,
        success: { ...state.success, contacts: payload }
      };

    case account.UPDATE_CONTACTS_FAIL:
      return {
        ...state,
        updateContactsFail: payload
      };

    case account.UPDATE_PASSWORD_SUCCESS:
      return {
        ...state,
        success: { ...state.success, password: payload }
      };

    case account.UPDATE_PASSWORD_FAIL:
      return {
        ...state,
        error: { ...state.error, password: payload }
      };

    case account.NOTIFICATIONS_V2_ACCOUNT_REQUEST:
      return { ...state, notificationsV2: { ...state.notificationsV2, accountLoading: true, accountError: null } };

    case account.NOTIFICATIONS_V2_ACCOUNT_SUCCESS:
      return {
        ...state,
        notificationsV2: {
          ...state.notificationsV2,
          rows: payload.rows,
          channels: payload.channels,
          smsAvailable: payload.smsAvailable,
          following: payload.following,
          accountLoading: false,
          accountError: null
        }
      };

    case account.NOTIFICATIONS_V2_ACCOUNT_FAIL:
      return { ...state, notificationsV2: { ...state.notificationsV2, accountLoading: false, accountError: payload } };

    case account.NOTIFICATIONS_V2_LISTINGS_REQUEST:
      return {
        ...state,
        notificationsV2: { ...state.notificationsV2, listings: { ...state.notificationsV2.listings, loading: true, error: null } }
      };

    case account.NOTIFICATIONS_V2_LISTINGS_SUCCESS:
      return {
        ...state,
        notificationsV2: { ...state.notificationsV2, listings: { items: payload.items, total: payload.total, loading: false, error: null } }
      };

    case account.NOTIFICATIONS_V2_LISTINGS_FAIL:
      return {
        ...state,
        notificationsV2: { ...state.notificationsV2, listings: { items: [], total: 0, loading: false, error: payload } }
      };

    case account.NOTIFICATIONS_V2_PREFERENCE_SAVING:
      return {
        ...state,
        notificationsV2: {
          ...state.notificationsV2,
          rows: setPreference(state.notificationsV2.rows, payload.notification, payload.channel, payload.value),
          rowState: { ...state.notificationsV2.rowState, [`${payload.notification}:${payload.channel}`]: "saving" },
          rowErrors: withoutKey(state.notificationsV2.rowErrors, `${payload.notification}:${payload.channel}`)
        }
      };

    case account.NOTIFICATIONS_V2_PREFERENCE_SAVED:
      return {
        ...state,
        notificationsV2: {
          ...state.notificationsV2,
          rows: setPreference(state.notificationsV2.rows, payload.notification, payload.channel, payload.value),
          rowState: { ...state.notificationsV2.rowState, [`${payload.notification}:${payload.channel}`]: "saved" }
        }
      };

    case account.NOTIFICATIONS_V2_PREFERENCE_CLEARED: {
      const cell = `${payload.notification}:${payload.channel}`;
      if (state.notificationsV2.rowState[cell] !== "saved") {
        return state;
      }

      return { ...state, notificationsV2: { ...state.notificationsV2, rowState: withoutKey(state.notificationsV2.rowState, cell) } };
    }

    case account.NOTIFICATIONS_V2_PREFERENCE_FAILED:
      return {
        ...state,
        notificationsV2: {
          ...state.notificationsV2,
          rows: setPreference(state.notificationsV2.rows, payload.notification, payload.channel, payload.value),
          rowState: { ...state.notificationsV2.rowState, [`${payload.notification}:${payload.channel}`]: "error" },
          rowErrors: {
            ...state.notificationsV2.rowErrors,
            [`${payload.notification}:${payload.channel}`]: { message: payload.message, status: payload.status }
          }
        }
      };

    case account.GET_NOTIFICATION_SETTINGS:
      return {
        ...state,
        notificationSettings: payload,
        preloaders: {
          ...state.preloaders,
          notificationSettings: false
        }
      };

    case account.GET_NOTIFICATION_SETTINGS_FAIL:
      return {
        ...state,
        notificationsFail: payload,
        preloaders: {
          ...state.preloaders,
          notificationSettings: false
        }
      };

    case account.UPDATE_NOTIFICATIONS_SUCCESS:
      return {
        ...state,
        success: { ...state.success, notifications: payload },
        preloaders: {
          ...state.preloaders,
          notifications: false
        }
      };

    case account.UPDATE_NEWSLETTERS_SUCCESS:
      return {
        ...state,
        success: { ...state.success, newsletters: payload },
        preloaders: {
          ...state.preloaders,
          newsletters: false
        }
      };

    case account.TOGGLE_ACTIVITY_OF_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          [payload.modal]: payload.isActive
        },
        verificationError: {
          ...defaultState.verificationError
        }
      };

    case account.CLEAR_ALL_MODALS:
      return {
        ...state,
        modals: {
          ...defaultState.modals
        },
        emailVerificationToken: "",
        phoneVerificationToken: ""
      };

    case account.EMAIL_VERIFY_CODE_SUCCESS:
      return {
        ...state,
        modals: {
          ...defaultState.modals,
          changeEmailVerification: true
        },
        preloaders: { ...state.preloaders, verifications: false },
        emailVerificationToken: payload?.token || ""
      };

    case account.EMAIL_SEND_VERIFY_CODE_SUCCESS:
      return {
        ...state,
        preloaders: { ...state.preloaders, verifications: false },
        emailVerificationToken: payload?.token || ""
      };

    case account.EMAIL_VERIFY_CODE_FAIL:
      return {
        ...state,
        modals: {
          ...defaultState.modals
        },
        preloaders: { ...state.preloaders, verifications: false },
        error: {
          ...state.error,
          changeEmail: payload
        },
        emailVerificationToken: ""
      };

    case account.EMAIL_VERIFY_CODE_CLEAR_ERROR:
      return {
        ...state,
        error: {
          ...state.error,
          changeEmail: {}
        },
        newLoginData: {
          ...defaultState.newLoginData
        }
      };

    case account.CHECK_EMAIL_VERIFY_CODE_FAIL:
      return {
        ...state,
        verificationError: {
          ...state.verificationError,
          checkEmailCode: payload || i18n.t("dashboard_verify_code_incorrect_caps")
        },
        preloaders: { ...state.preloaders, verifications: false }
      };
    case account.UPDATE_EMAIL_SUCCESS:
      return {
        ...state,
        modals: {
          ...defaultState.modals,
          changeEmailSuccess: true
        },
        verificationError: {
          ...defaultState.verificationError
        },
        newLoginData: {
          email: "",
          password: ""
        },
        preloaders: { ...state.preloaders, verifications: false },
        profile: {
          ...state.profile,
          email: payload
        },
        emailVerificationToken: ""
      };

    case account.VERIFIED_EMAIL_SUCCESS:
      return {
        ...state,
        modals: {
          ...defaultState.modals,
          verifyEmailSuccess: true
        },
        verificationError: {
          ...defaultState.verificationError
        },
        newLoginData: {
          email: "",
          password: ""
        },
        preloaders: { ...state.preloaders, verifications: false },
        profile: {
          ...state.profile,
          email: payload
        },
        emailVerificationToken: ""
      };

    case account.UPDATE_EMAIL_FAIL:
      return {
        ...state,
        verificationError: {
          ...state.verificationError,
          changeEmail: true
        },
        preloaders: { ...state.preloaders, verifications: false }
      };

    case account.EMAIL_VERIFICATION_CHANGE:
      return {
        ...state,
        modals: {
          ...defaultState.modals,
          changeEmailNew: true
        },
        preloaders: { ...state.preloaders, verifications: false },
        emailVerificationToken: ""
      };

    case account.PHONE_VERIFICATION_CHANGE:
      return {
        ...state,
        modals: {
          ...defaultState.modals,
          addPhone: true
        },
        preloaders: { ...state.preloaders, verifications: false },
        phoneVerificationToken: ""
      };

    case account.PASSWORD_VERIFICATION_SUCCESS:
      return {
        ...state,
        modals: {
          ...defaultState.modals,
          changePasswordSuccess: true
        },
        preloaders: { ...state.preloaders, verifications: false }
      };

    case account.SAVE_NEW_EMAIL_PASSWORD:
      return {
        ...state,
        newLoginData: {
          email: payload.email,
          password: payload.password
        }
      };
    case account.CLEAN_NEW_EMAIL_PASSWORD:
      return {
        ...state,
        newLoginData: {
          email: "",
          password: ""
        },
        verificationError: {
          ...defaultState.verificationError
        },
        emailVerificationToken: ""
      };
    case account.PHONE_VERIFY_CODE_SUCCESS:
      return {
        ...state,
        preloaders: { ...state.preloaders, verifications: false },
        verificationError: {
          ...defaultState.verificationError
        },
        phoneVerificationToken: payload?.token || ""
      };
    case account.PHONE_VERIFY_CODE_FAIL:
      return {
        ...state,
        preloaders: { ...state.preloaders, verifications: false },
        phoneVerificationToken: ""
      };
    case account.CHECK_PHONE_VERIFY_CODE_FAIL:
      return {
        ...state,
        preloaders: { ...state.preloaders, verifications: false },
        verificationError: {
          ...state.verificationError,
          checkPhoneCode: payload || i18n.t("dashboard_verify_code_incorrect")
        }
      };

    case account.UPDATE_PHONE_SUCCESS:
      return {
        ...state,
        modals: {
          ...defaultState.modals,
          addPhoneSuccess: true
        },
        verificationError: {
          ...defaultState.verificationError
        },
        preloaders: { ...state.preloaders, verifications: false },
        phoneVerificationToken: ""
      };

    case account.REMOVE_PHONE_SUCCESS:
      return {
        ...state,
        modals: {
          ...defaultState.modals,
          removePhoneSuccess: true
        },
        preloaders: { ...state.preloaders, verifications: false }
      };

    case account.REMOVE_PHONE_FAIL:
      return {
        ...state,
        preloaders: { ...state.preloaders, verifications: false }
      };

    case account.NOTIFICATION_REMOVE_EMAIL_SUCCESS:
      // eslint-disable-next-line no-case-declarations
      const removeEmailNotification = state.notificationSettings.listingNotifications.listings.map(item => {
        if (String(item.listing_id) === String(payload.listingId)) {
          return {
            ...item,
            verifiedemails: item.verifiedemails.filter(emailItem => String(emailItem.prkey) !== String(payload.prKey))
          };
        }

        return item;
      });

      return {
        ...state,
        preloaders: { ...state.preloaders, removeNotificationEmail: false },
        modals: { ...defaultState.modals, deleteEmailSuccess: true },
        notificationSettings: {
          ...state.notificationSettings,
          listingNotifications: {
            ...state.notificationSettings.listingNotifications,
            listings: removeEmailNotification
          }
        }
      };

    case account.NOTIFICATION_REMOVE_EMAIL_FAIL:
      return {
        ...state,
        preloaders: { ...state.preloaders, removeNotificationEmail: false }
      };

    case account.NOTIFICATION_REMOVE_PHONE_SUCCESS:
      // eslint-disable-next-line no-case-declarations
      const removePhoneNotification = state.notificationSettings.listingNotifications.listings.map(item => {
        if (String(item.listing_id) === String(payload.listingId)) {
          return {
            ...item,
            verifiedphones: item.verifiedphones.filter(emailItem => String(emailItem.prkey) !== String(payload.prKey))
          };
        }

        return item;
      });

      return {
        ...state,
        preloaders: { ...state.preloaders, removeNotificationPhone: false },
        modals: { ...defaultState.modals, deletePhoneSuccess: true },
        notificationSettings: {
          ...state.notificationSettings,
          listingNotifications: {
            ...state.notificationSettings.listingNotifications,
            listings: removePhoneNotification
          }
        }
      };

    case account.NOTIFICATION_REMOVE_PHONE_FAIL:
      return {
        ...state,
        preloaders: { ...state.preloaders, removeNotificationPhone: false }
      };

    case account.MARK_READ_ALL_NOTIFICATION:
      return {
        ...state,
        profile: {
          ...state.profile,
          notifications: state.profile.notifications.map(item => {
            return { ...item, is_viewed: "1" };
          })
        }
      };

    case account.MARK_READ_NOTIFICATION:
      return {
        ...state,
        profile: {
          ...state.profile,
          notifications: state.profile.notifications.map(item => {
            return payload.includes(item.notification_id) ? { ...item, is_viewed: "1" } : item;
          })
        }
      };

    case account.MARK_UN_READ_NOTIFICATION:
      return {
        ...state,
        profile: {
          ...state.profile,
          notifications: state.profile.notifications.map(item => {
            return payload.includes(item.notification_id) ? { ...item, is_viewed: 0 } : item;
          })
        }
      };

    default:
      return state;
  }
}
